/*
 * Branded Best: company orders (sections/bb-company-order.liquid).
 *
 * A quote request in four steps that ends in Shopify's own contact form. No build step, no libraries.
 * The first half of this file is PURE logic (dates, minimums, allocation totals, the summary text) and is
 * exported for node tests (proposals/01-company-orders/test.js). The second half wires it to the page.
 * With JavaScript on, the post carries name, email, company, phone and one readable summary in contact[body]
 * (the raw step fields are dropped so nothing is sent twice). With JavaScript off the section still works:
 * every step renders open and the form posts its raw fields.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Pure logic                                                          */
  /* ------------------------------------------------------------------ */

  // Dates come from assets/bb-dates.js (window.BBDates), shared with the in-hands date on product pages.
  var D = typeof BBDates !== 'undefined' ? BBDates : null;
  if (!D) return;
  var LEAD_DAYS = D.LEAD_DAYS;
  var EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

  var TEXT = {
    approveNone: 'Pick your in-hands date and we’ll show the date to approve your proof by.',
    approveNoneReleases: 'Add a release date and we’ll show the date to approve your proof by.',
    season: 'Your date falls in our busiest weeks, so this includes two extra weeks.',
    notSet: 'Not set yet',
    nothing: 'Nothing added yet',
    later: 'Sending it later',
    floorSum: 'Sooner than we can promise',
    blockNone: 'Add at least one piece first',
    blockMin: 'Add at least one piece at its minimum first',
    blockSplit: 'Assign every piece to an address first',
    blockReleases: 'Schedule every piece first',
    blockEmail: 'Add your work email first',
    fewerNote: 'For fewer, add a note in step 4 and our team will tell you what’s possible.'
  };

  function pad(n) { return (n < 10 ? '0' : '') + n; }
  function num(v) {
    var n = parseInt(String(v == null ? '' : v).replace(/,/g, ''), 10);
    return isNaN(n) || n < 0 ? 0 : n;
  }
  // Thousands separators wherever a count is shown ("2,000 cards").
  function fmtN(n) { return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }
  function plural(n, one, many) { return fmtN(n) + ' ' + (n === 1 ? one : many); }
  function cap(s) { s = String(s || ''); return s.charAt(0).toUpperCase() + s.slice(1); }
  function trim(s) { return String(s == null ? '' : s).trim(); }

  var ymd = D.ymd, parseISO = D.parseISO, toISO = D.toISO, addDays = D.addDays, addMonths = D.addMonths;
  var fmtDate = D.fmtDate, todayISO = D.todayISO, inSeason = D.inSeason, nextBusinessDay = D.nextBusinessDay;
  var approveByDate = D.approveByDate, canPromise = D.canPromise, earliestInhand = D.earliestInhand;
  function approveInfo(base, today, releases) {
    if (!parseISO(base)) {
      return { state: 'none', line: releases ? TEXT.approveNoneReleases : TEXT.approveNone, sum: TEXT.notSet, season: false, date: '' };
    }
    var a = approveByDate(base);
    if (!canPromise(base, today)) {
      var earliest = earliestInhand(today);
      return {
        state: 'floor',
        line: 'That date is sooner than we can promise. Our earliest in-hands date is about ' + fmtDate(earliest) +
          '. Tell our team your date anyway, and we’ll tell you right away what’s possible.',
        sum: TEXT.floorSum,
        season: false,
        earliest: earliest,
        date: ''
      };
    }
    return {
      state: 'ok',
      line: (releases ? 'For the first release, approve your proof by ' : 'For this date, approve your proof by ') + fmtDate(a) + '.',
      sum: fmtDate(a),
      season: inSeason(base),
      date: a
    };
  }
  // The first release: the in-hands date when one is set and we can promise it; otherwise today + 42 days,
  // rounded up to the next 1st of a month (moved on a month at a time while that's sooner than we can promise).
  function firstReleaseDate(inhand, today) {
    if (parseISO(inhand) && canPromise(inhand, today)) return inhand;
    var e = parseISO(addDays(today, LEAD_DAYS));
    var m = e.getUTCMonth() + (e.getUTCDate() === 1 ? 0 : 1);
    var d = ymd(e.getUTCFullYear() + Math.floor(m / 12), m % 12, 1);
    for (var i = 0; i < 24 && !canPromise(d, today); i++) d = addMonths(d, 1);
    return d;
  }

  // Pieces come from the section's blocks. A per-person piece (business cards) is ordered as
  // people × units each; when its unit names a count ("box of 50 cards") the math shows the total.
  function normalizeConfig(raw) {
    var pieces = ((raw && raw.pieces) || []).map(function (p) {
      var one = trim(p.one) || 'piece';
      var many = trim(p.many) || one + 's';
      var m = /(\d[\d,]*)\s+([^\s\d]+)\s*$/.exec(one);
      return {
        id: String(p.id),
        handle: trim(p.handle),
        title: trim(p.title),
        short: trim(p.short) || trim(p.title),
        min: Math.max(num(p.min), 1),
        one: one,
        many: many,
        perPerson: !!p.perPerson,
        per: m ? num(m[1]) : 0,
        perNoun: m ? m[2] : '',
        addonOne: trim(p.addonOne),
        addonMany: trim(p.addonMany),
        note: trim(p.note)
      };
    });
    return { pieces: pieces, email: trim(raw && raw.email) };
  }
  function pieceById(cfg, id) {
    for (var i = 0; i < cfg.pieces.length; i++) if (cfg.pieces[i].id === id) return cfg.pieces[i];
    return null;
  }
  // Products, not unit words, name the columns and lines: several pieces share a unit ("sets", "wallets").
  function pieceName(p) { return trim(p.short || p.title || '') || cap(p.many); }
  function colLabel(p) { return pieceName(p).replace(/^The /, ''); }

  // The order as lines, in block order. A piece is in the order when its quantity (or people) is above 0.
  function linesOf(state, cfg) {
    var out = [];
    cfg.pieces.forEach(function (p) {
      var q = num(state.qty[p.id]);
      if (q <= 0) return;
      if (p.perPerson) {
        var b = num(state.boxes[p.id] == null ? 1 : state.boxes[p.id]);
        out.push({ id: p.id, piece: p, people: q, boxes: b, units: q * b });
      } else {
        out.push({ id: p.id, piece: p, qty: q, units: q });
      }
    });
    return out;
  }
  function meetsMin(line) {
    var p = line.piece;
    if (p.perPerson) return line.boxes >= 1 && (p.per ? line.boxes * p.per >= p.min : line.boxes >= p.min);
    return line.qty >= p.min;
  }
  function namesMath(line) {
    var p = line.piece;
    var s = fmtN(line.people) + ' people × ' + plural(line.boxes, p.one, p.many);
    if (p.per) return s + ' = ' + fmtN(line.people * line.boxes * p.per) + ' ' + p.perNoun;
    return s + ' = ' + plural(line.units, p.one, p.many);
  }
  function pieceWords(state, lines) {
    var out = [];
    lines.forEach(function (l) {
      var p = l.piece;
      if (p.perPerson) {
        out.push(pieceName(p) + ': ' + fmtN(l.people) + ' names × ' + plural(l.boxes, p.one, p.many));
      } else {
        out.push(pieceName(p) + ': ' + plural(l.qty, p.one, p.many));
        if (p.addonMany && state.addon[p.id]) out.push(pieceName(p) + ': ' + plural(l.qty, p.addonOne || p.addonMany, p.addonMany));
      }
    });
    return out;
  }
  function belowMinLines(lines) {
    var out = [];
    lines.forEach(function (l) {
      if (meetsMin(l)) return;
      var p = l.piece;
      if (p.perPerson) out.push('Each name needs at least ' + (p.per ? fmtN(p.min) + ' ' + p.perNoun : plural(p.min, p.one, p.many)) + '.');
      else out.push(pieceName(p) + ' starts at ' + plural(p.min, p.one, p.many) + '.');
    });
    return out;
  }
  function qtyWords(q, lines) {
    return lines.map(function (l) {
      var n = num(q[l.id]);
      return n ? colLabel(l.piece) + ' × ' + fmtN(n) : '';
    }).filter(Boolean).join(', ');
  }
  // Per piece: how many are assigned (or scheduled) against how many are ordered.
  function tally(rows, lines, done, left) {
    return lines.map(function (l) {
      var p = l.piece, need = l.units;
      var got = rows.reduce(function (a, r) { return a + num(r.q[l.id]); }, 0);
      var ok = got === need;
      var who = colLabel(p) + ': ';
      var text = who + (ok ? 'all ' + fmtN(need) + ' ' + done
        : got < need ? fmtN(need - got) + ' left to ' + left
        : fmtN(got - need) + ' more ' + done + ' than ordered');
      return { id: l.id, ok: ok, text: text, got: got, need: need };
    });
  }
  // Monthly = 12 releases a month apart, quarterly = 4 releases three months apart.
  // Each piece is split evenly; any remainder goes on the first release.
  function presetRows(preset, lines, startId, firstDate) {
    var n = preset === 'monthly' ? 12 : 4, step = preset === 'monthly' ? 1 : 3;
    var rows = [];
    for (var i = 0; i < n; i++) rows.push({ id: startId + i, date: addMonths(firstDate, i * step), q: {} });
    lines.forEach(function (l) {
      var base = Math.floor(l.units / n), rem = l.units - base * n;
      rows.forEach(function (r, i) { r.q[l.id] = String(base + (i === 0 ? rem : 0)); });
    });
    return rows;
  }
  function firstDropDate(state) {
    return state.drops.map(function (r) { return r.date; }).filter(function (d) { return !!parseISO(d); }).sort()[0] || '';
  }
  function emailOk(state) { return EMAIL_RE.test(trim(state.email)); }
  function logoSummary(state, cfg) {
    if (trim(state.logoUrl)) return 'Link added';
    if (state.logoEmail) return cfg.email ? 'Emailing it to ' + cfg.email : 'Emailing it';
    return TEXT.later;
  }

  function initialState(cfg, today) {
    var st = {
      step: 1, reach: 1,
      qty: {}, boxes: {}, addon: {}, names: {}, namesSheet: {},
      logoUrl: '', logoEmail: false, storedLogo: '',
      inhand: '', ship: 'one',
      oneTo: '', oneStreet: '', oneNotes: '',
      addrs: [{ id: 1, label: '', street: '', q: {} }, { id: 2, label: '', street: '', q: {} }],
      preset: 'quarterly', drops: [], relTo: '',
      name: '', email: '', company: '', phone: '', procurement: '', po: false, w9: false, coi: false, notes: '', optin: false, kit: false,
      summary: false, remind: false,
      nextId: 3
    };
    cfg.pieces.forEach(function (p) { if (p.perPerson) st.boxes[p.id] = '1'; });
    st.drops = presetRows(st.preset, [], st.nextId, firstReleaseDate('', today));
    st.nextId += st.drops.length;
    return st;
  }
  function clone(st) {
    var o = {};
    Object.keys(st).forEach(function (k) { o[k] = st[k]; });
    return o;
  }
  function withMap(map, key, value) {
    var o = {};
    Object.keys(map).forEach(function (k) { o[k] = map[k]; });
    o[key] = value;
    return o;
  }
  // Change a quantity (qty = pieces, or people for a per-person piece) or boxes each. Allocations of a piece
  // that leaves the order are cleared, and a schedule that still comes straight from a preset is re-split.
  function setPieces(state, patch, cfg, today) {
    var st = clone(state);
    if (patch.qty) Object.keys(patch.qty).forEach(function (k) { st.qty = withMap(st.qty, k, patch.qty[k]); });
    if (patch.boxes) Object.keys(patch.boxes).forEach(function (k) { st.boxes = withMap(st.boxes, k, patch.boxes[k]); });
    var have = linesOf(st, cfg).map(function (l) { return l.id; });
    var strip = function (r) {
      var q = {};
      Object.keys(r.q).forEach(function (k) { if (have.indexOf(k) >= 0) q[k] = r.q[k]; });
      var o = clone(r); o.q = q; return o;
    };
    st.addrs = st.addrs.map(strip);
    st.drops = st.drops.map(strip);
    if (st.preset) {
      st.drops = presetRows(st.preset, linesOf(st, cfg), st.nextId, firstReleaseDate(st.inhand, today));
      st.nextId += st.drops.length;
    }
    return st;
  }
  // The in-hands date; a schedule that still comes straight from a preset moves with it.
  function setInhand(state, value, cfg, today) {
    var st = clone(state);
    st.inhand = value;
    if (st.preset) {
      st.drops = presetRows(st.preset, linesOf(st, cfg), st.nextId, firstReleaseDate(value, today));
      st.nextId += st.drops.length;
    }
    return st;
  }
  function pickPreset(state, preset, cfg, today) {
    var st = clone(state);
    st.preset = preset;
    st.drops = presetRows(preset, linesOf(st, cfg), st.nextId, firstReleaseDate(st.inhand, today));
    st.nextId += st.drops.length;
    return st;
  }
  function setShip(state, ship, cfg, today) {
    var st = clone(state);
    st.ship = ship;
    if (ship === 'split' && !st.addrs.length) {
      st.addrs = [{ id: st.nextId, label: '', street: '', q: {} }, { id: st.nextId + 1, label: '', street: '', q: {} }];
      st.nextId += 2;
    }
    if (ship === 'releases' && !st.drops.length) return pickPreset(st, 'quarterly', cfg, today);
    return st;
  }
  function setCell(state, field, rowId, pieceId, value) {
    var st = clone(state);
    st[field] = st[field].map(function (r) {
      if (r.id !== rowId) return r;
      var o = clone(r); o.q = withMap(r.q, pieceId, value); return o;
    });
    if (field === 'drops') st.preset = '';
    return st;
  }
  function setRowField(state, field, rowId, key, value) {
    var st = clone(state);
    st[field] = st[field].map(function (r) {
      if (r.id !== rowId) return r;
      var o = clone(r); o[key] = value; return o;
    });
    if (field === 'drops' && key === 'date') st.preset = '';
    return st;
  }
  function addAddress(state) {
    var st = clone(state);
    st.addrs = st.addrs.concat([{ id: st.nextId, label: '', street: '', q: {} }]);
    st.nextId += 1;
    return st;
  }
  function removeAddress(state, id) {
    var st = clone(state);
    if (st.addrs.length > 1) st.addrs = st.addrs.filter(function (r) { return r.id !== id; });
    return st;
  }
  function addRelease(state, today) {
    var st = clone(state);
    var last = st.drops.map(function (r) { return r.date; }).filter(function (d) { return !!parseISO(d); }).sort().pop();
    var date = last ? addMonths(last, 1) : firstReleaseDate(st.inhand, today);
    st.drops = st.drops.concat([{ id: st.nextId, date: date, q: {} }]);
    st.nextId += 1;
    st.preset = '';
    return st;
  }
  function removeRelease(state, id) {
    var st = clone(state);
    st.drops = st.drops.filter(function (r) { return r.id !== id; });
    st.preset = '';
    return st;
  }
  function go(state, n) {
    var st = clone(state);
    st.reach = Math.max(st.reach || 1, st.step || 1, n);
    st.step = n;
    return st;
  }

  // Everything the page shows, derived from the state. No DOM here.
  function view(state, cfg, today) {
    var lines = linesOf(state, cfg);
    var words = pieceWords(state, lines);
    var below = belowMinLines(lines);
    var anyAtMin = lines.some(meetsMin);
    var releases = state.ship === 'releases';
    var firstRel = firstDropDate(state);
    var base = releases ? firstRel : state.inhand;
    var approve = approveInfo(base, today, releases);
    var reach = Math.max(state.reach || 1, state.step || 1);

    var names = {};
    lines.forEach(function (l) { if (l.piece.perPerson) names[l.id] = namesMath(l); });

    var splitTotals = tally(state.addrs, lines, 'assigned', 'assign');
    var dropTotals = tally(state.drops, lines, 'scheduled', 'schedule');
    var reasons = [];
    if (!lines.length) reasons.push(TEXT.blockNone);
    else if (!anyAtMin) reasons.push(TEXT.blockMin);
    if (state.ship === 'split' && lines.length && splitTotals.some(function (t) { return !t.ok; })) reasons.push(TEXT.blockSplit);
    if (releases && lines.length && dropTotals.some(function (t) { return !t.ok; })) reasons.push(TEXT.blockReleases);
    var mailOk = emailOk(state);
    // The panel shows only real problems, and none while the buyer is still on a fresh step 1;
    // the missing-email line waits until step 4 (or a submit attempt).
    var panelLines = reach > 1 || state.showAll ? reasons.slice() : [];
    if (!mailOk && (state.step === 4 || state.showAll)) panelLines.push(TEXT.blockEmail);
    var blocked = reasons.length > 0 || !mailOk;

    var delivery = state.ship === 'split' ? plural(state.addrs.length, 'address', 'addresses')
      : releases ? plural(state.drops.length, 'release', 'releases') : 'One address';
    var where = [];
    if (state.ship === 'split') {
      state.addrs.forEach(function (a, i) {
        var w = qtyWords(a.q, lines);
        if (w) where.push((trim(a.label) || 'Address ' + (i + 1)) + ': ' + w);
      });
    } else if (releases) {
      var dated = state.drops.slice().sort(function (a, b) { return a.date < b.date ? -1 : a.date > b.date ? 1 : 0; });
      var showN = dated.length > 4 ? 3 : dated.length;
      dated.slice(0, showN).forEach(function (r) { where.push((fmtDate(r.date) || 'No date yet') + ': ' + (qtyWords(r.q, lines) || '0')); });
      if (dated.length > showN) where.push('and ' + (dated.length - showN) + ' more');
    }

    var s3 = releases ? delivery + (firstRel ? ', the first by ' + fmtDate(firstRel) : '')
      : delivery + (parseISO(state.inhand) ? ', in hand by ' + fmtDate(state.inhand) : '');
    var s4 = [trim(state.name), trim(state.company)].filter(Boolean).join(', ') || trim(state.email);
    var SUM = { 1: words.length ? words.join(', ') : TEXT.nothing, 2: logoSummary(state, cfg), 3: s3, 4: s4 };
    var steps = {};
    [1, 2, 3, 4].forEach(function (n) {
      var open = state.step === n;
      var done = !open && (n < reach || (n === 4 && mailOk));
      steps[n] = { open: open, closed: !open, done: done, canEdit: done, waiting: !open && !done, summary: done ? SUM[n] : '' };
    });

    var tail = TEXT.fewerNote;
    if (!anyAtMin && cfg.email) tail = 'For fewer, email ' + cfg.email + ' and our team will tell you what’s possible.';

    return {
      lines: lines,
      ids: lines.map(function (l) { return l.id; }),
      pieceWords: words,
      sumPieces: words.length ? words : [TEXT.nothing],
      names: names,
      belowMin: below,
      hasBelowMin: below.length > 0,
      belowMinText: below.length ? below.join(' ') + ' ' + tail : '',
      releases: releases,
      firstRelease: firstRel,
      base: base,
      approve: approve,
      seasonLine: approve.state === 'ok' && approve.season,
      inhandLabel: releases ? 'First release by' : 'In hand by',
      sumInhand: parseISO(base) ? fmtDate(base) : TEXT.notSet,
      sumApprove: approve.sum,
      sumLogo: trim(state.logoUrl) || state.logoEmail || reach > 2 ? logoSummary(state, cfg) : TEXT.nothing,
      delivery: delivery,
      where: where,
      splitTotals: splitTotals,
      dropTotals: dropTotals,
      cols: lines.map(function (l) { return { id: l.id, label: colLabel(l.piece) }; }),
      reasons: reasons,
      panelLines: panelLines,
      emailOk: mailOk,
      blocked: blocked,
      ready: !blocked,
      steps: steps
    };
  }

  function paperwork(state) {
    var out = [];
    if (state.summary) out.push('One-page summary for sign-off');
    if (state.po) out.push('Purchase order');
    if (state.w9) out.push('W-9');
    if (state.coi) out.push('Certificate of insurance');
    return out;
  }
  function nameList(text) {
    return String(text || '').split(/\r?\n/).map(trim).filter(Boolean);
  }
  function namesFor(state, line) {
    if (state.namesSheet[line.id]) return 'Sending a spreadsheet of names and titles';
    var list = nameList(state.names[line.id]);
    return list.length ? list.join('; ') : 'Not added yet';
  }
  function addressLine(to, street, notes) {
    var s = [trim(to), trim(street)].filter(Boolean).join(', ');
    if (trim(notes)) s += (s ? ' ' : '') + '(' + trim(notes) + ')';
    return s;
  }

  // The order as labelled values (Pieces, Logo, Delivery, …). They build the contact[body] summary below.
  function orderFields(state, cfg, today) {
    var v = view(state, cfg, today);
    var f = {};
    f['Pieces'] = v.pieceWords.join('; ') || 'None';
    var ppl = v.lines.filter(function (l) { return l.piece.perPerson; });
    if (ppl.length) {
      f['Names'] = ppl.map(function (l) {
        return (ppl.length > 1 ? l.piece.short + ': ' : '') + namesFor(state, l);
      }).join(' | ');
    }
    f['Logo'] = trim(state.logoUrl) || (state.logoEmail ? 'Emailing it' + (cfg.email ? ' to ' + cfg.email : '') : TEXT.later);
    if (v.releases) f['In-hands date'] = v.firstRelease ? 'First release by ' + fmtDate(v.firstRelease) : 'Not set';
    else f['In-hands date'] = parseISO(state.inhand) ? fmtDate(state.inhand) : 'Not set';
    f['Approve proof by'] = v.approve.state === 'ok' ? v.approve.sum
      : v.approve.state === 'floor' ? 'Sooner than we can promise (earliest in-hands about ' + fmtDate(v.approve.earliest) + ')' : 'Not set';
    if (state.ship === 'one') {
      f['Delivery'] = 'One address';
      f['Addresses'] = addressLine(state.oneTo, state.oneStreet, state.oneNotes) || 'Not given yet';
    } else if (state.ship === 'split') {
      f['Delivery'] = 'Several addresses (' + state.addrs.length + ')';
      f['Addresses'] = state.addrs.map(function (a, i) {
        return (i + 1) + '. ' + (addressLine(a.label, a.street) || 'Address ' + (i + 1)) + ': ' + (qtyWords(a.q, v.lines) || 'nothing assigned');
      }).join(' | ');
    } else {
      f['Delivery'] = 'Scheduled releases (' + state.drops.length + ')';
      f['Addresses'] = trim(state.relTo) || 'Not given yet';
      f['Releases'] = state.drops.slice().sort(function (a, b) { return a.date < b.date ? -1 : a.date > b.date ? 1 : 0; })
        .map(function (r) { return (fmtDate(r.date) || 'No date yet') + ': ' + (qtyWords(r.q, v.lines) || '0'); }).join(' | ');
    }
    if (state.remind) f['Reminder'] = v.approve.state === 'ok' ? 'Two weeks before the approve-by date' : 'Asked for, but no in-hands date yet';
    f['Paperwork'] = paperwork(state).join(', ') || 'None requested';
    if (state.kit) f['Packing'] = 'One box per person';
    if (state.source) f['From'] = state.source;
    return f;
  }

  // The readable multi-line summary that goes in contact[body].
  function orderBody(state, cfg, today) {
    var v = view(state, cfg, today);
    var f = orderFields(state, cfg, today);
    var out = ['Company order request', '', 'Pieces'];
    if (v.pieceWords.length) v.pieceWords.forEach(function (w) { out.push('- ' + w); });
    else out.push('- None');
    v.lines.forEach(function (l) {
      if (!l.piece.perPerson) return;
      out.push('- ' + namesMath(l));
      out.push('', 'Names for ' + l.piece.short);
      if (state.namesSheet[l.id]) out.push('Sending a spreadsheet of names and titles.');
      else {
        var list = nameList(state.names[l.id]);
        if (list.length) list.forEach(function (n) { out.push(n); });
        else out.push('Not added yet.');
      }
    });
    if (v.belowMin.length) out.push('', 'Below minimum: ' + v.belowMin.join(' '));
    out.push('', 'Logo: ' + f['Logo']);
    if (state.storedLogo) out.push('Logo added on the site: ' + state.storedLogo + ' (the form can\'t send the file)');
    out.push('', 'Delivery: ' + f['Delivery']);
    if (state.ship === 'split') {
      f['Addresses'].split(' | ').forEach(function (a) { out.push(a); });
      v.splitTotals.forEach(function (t) { out.push('(' + t.text + ')'); });
    } else if (state.ship === 'releases') {
      f['Releases'].split(' | ').forEach(function (r) { out.push('- ' + r); });
      out.push('Deliver releases to: ' + f['Addresses']);
    } else {
      out.push('Deliver to: ' + f['Addresses']);
    }
    if (f['Packing']) out.push('Packing: ' + f['Packing']);
    out.push('In-hands date: ' + f['In-hands date']);
    out.push('Approve proof by: ' + f['Approve proof by']);
    if (f['Reminder']) out.push('Reminder: ' + f['Reminder']);
    out.push('', 'Paperwork: ' + f['Paperwork']);
    out.push('', 'Contact: ' + ([trim(state.name), trim(state.company), trim(state.email), trim(state.phone)].filter(Boolean).join(', ') || 'Not given'));
    if (state.source) out.push('From: ' + state.source);
    if (trim(state.procurement)) out.push('Procurement contact: ' + trim(state.procurement));
    if (trim(state.notes)) out.push('Notes: ' + trim(state.notes));
    out.push('Send new pieces and ideas: ' + (state.optin ? 'Yes' : 'No'));
    return out.join('\n');
  }

  // House: a link from a prospect's private page carries ?company=<name>. Fill the company field once (never over
  // what the visitor typed), as plain text, and note where the request came from.
  // ?company=Northline&pieces=the-metal-card,the-luxury-pen: the company fills in, and the pieces picked on their
  // private page (product handles, at most 3, only pieces on this page's list) are listed first and named in the email.
  function prefillFromUrl(state, search, cfg) {
    var m = /[?&]company=([^&#]*)/.exec(search || '');
    if (!m) return state;
    var name;
    try { name = decodeURIComponent(m[1].replace(/\+/g, ' ')); } catch (e) { return state; }
    name = name.replace(/[\u0000-\u001f\u007f<>]/g, '').replace(/\s+/g, ' ').trim().slice(0, 80).trim();
    if (!name) return state;
    var st = clone(state);
    if (!trim(st.company)) st.company = name;
    st.source = 'their private page (' + name + ')';
    var pm = /[?&]pieces=([^&#]*)/.exec(search || '');
    var picked = [];
    if (pm && cfg) {
      var handles;
      try { handles = decodeURIComponent(pm[1]).split(','); } catch (e2) { handles = []; }
      handles.forEach(function (h) {
        h = trim(h).toLowerCase();
        var p = cfg.pieces.filter(function (x) { return x.handle && x.handle === h; })[0];
        if (p && picked.indexOf(p.id) < 0 && picked.length < 3) picked.push(p.id);
      });
    }
    if (picked.length) {
      st.picked = picked;
      st.source += ', picked there: ' + picked.map(function (id) { return pieceById(cfg, id).short; }).join(', ');
    }
    return st;
  }

  // The short list shown on the confirmation after the form posts.
  function confirmLines(state, cfg, today) {
    var v = view(state, cfg, today);
    var out = v.pieceWords.slice();
    out.push('Logo: ' + logoSummary(state, cfg));
    out.push('Delivery: ' + v.delivery);
    if (state.kit) out.push('Packing: one box per person');
    out.push(v.inhandLabel + ': ' + v.sumInhand);
    out.push('Approve proof by: ' + v.sumApprove);
    return out;
  }

  var api = {
    LEAD_DAYS: LEAD_DAYS, SEASON_DAYS: D.SEASON_DAYS, TEXT: TEXT,
    num: num, fmtN: fmtN, plural: plural, parseISO: parseISO, addDays: addDays, addMonths: addMonths, fmtDate: fmtDate,
    todayISO: todayISO, inSeason: inSeason, nextBusinessDay: nextBusinessDay, approveByDate: approveByDate,
    canPromise: canPromise, earliestInhand: earliestInhand, approveInfo: approveInfo, firstReleaseDate: firstReleaseDate,
    normalizeConfig: normalizeConfig, linesOf: linesOf, meetsMin: meetsMin, namesMath: namesMath, pieceWords: pieceWords,
    tally: tally, presetRows: presetRows, initialState: initialState, setPieces: setPieces, setInhand: setInhand,
    pickPreset: pickPreset, setShip: setShip, setCell: setCell, setRowField: setRowField, addAddress: addAddress,
    removeAddress: removeAddress, addRelease: addRelease, removeRelease: removeRelease, go: go, view: view,
    orderFields: orderFields, orderBody: orderBody, confirmLines: confirmLines, prefillFromUrl: prefillFromUrl
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;

  /* ------------------------------------------------------------------ */
  /* Page wiring                                                         */
  /* ------------------------------------------------------------------ */

  if (typeof window === 'undefined' || typeof document === 'undefined' || !window.customElements) return;

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function store() {
    try { return window.sessionStorage; } catch (e) { return null; }
  }

  function mount(root) {
    var cfgEl = root.querySelector('script[data-bb-co-config]');
    var form = root.querySelector('form');
    if (!cfgEl || !form) return null;
    var cfg;
    try { cfg = normalizeConfig(JSON.parse(cfgEl.textContent)); } catch (e) { return null; }
    var today = todayISO();
    var uid = root.getAttribute('data-uid') || 'bb-co';
    var draftKey = 'bb-co:' + window.location.pathname + ':' + uid;
    var state = initialState(cfg, today);

    function $(sel) { return root.querySelector(sel); }
    function $$(sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }

    // After a successful post: show what was sent, then forget the draft.
    if (root.querySelector('[data-bb-co-posted]')) {
      var ss = store(), saved = null;
      try { saved = ss && JSON.parse(ss.getItem(draftKey) || 'null'); } catch (e) { saved = null; }
      if (saved && saved.confirm) {
        var who = $('[data-confirm-name]');
        if (who && saved.confirm.first) who.textContent = ', ' + saved.confirm.first;
        var list = $('[data-confirm-lines]');
        if (list) {
          list.innerHTML = saved.confirm.lines.map(function (l) { return '<li>' + esc(l) + '</li>'; }).join('');
          list.hidden = false;
        }
      }
      try { if (ss) ss.removeItem(draftKey); } catch (e) { /* storage blocked */ }
      return {};
    }

    root.classList.add('is-enhanced');
    // House: the phone bar steps aside while the summary (with its own button) is on screen.
    var sumEl = root.querySelector('.bb-co__summary');
    if (sumEl && root.querySelector('[data-co-bar]') && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (es) {
        var e = es[es.length - 1];
        // on screen, or already scrolled past (then the bar would only cover the footer)
        root.classList.toggle('is-sum-in', e.isIntersecting || e.boundingClientRect.top < 0);
      }, { rootMargin: '0px 0px -40px 0px' }).observe(sumEl);
    }
    form.noValidate = true; // JS validates; hidden steps can't show the browser's bubbles
    $$('input[type="date"]').forEach(function (el) { el.min = today; });
    $$('[data-js-only]').forEach(function (el) { el.hidden = false; });

    // House: a logo added anywhere on the site (the logo tool keeps it in this browser) is named in step 2, and
    // "I'll email it" is ticked for them, since this form can't carry the file.
    if (window.BBHouseLogo) {
      window.BBHouseLogo.peek().then(function (rec) {
        if (!rec) return;
        var box = root.querySelector('[data-co-stored]');
        if (!box) return;
        var img = box.querySelector('[data-co-stored-img]');
        try { if (img && rec.blob && /^image\//.test(rec.blob.type || '')) img.src = URL.createObjectURL(rec.blob); else if (img) img.hidden = true; } catch (e) { if (img) img.hidden = true; }
        var nm = box.querySelector('[data-co-stored-name]'); if (nm) nm.textContent = rec.name;
        box.hidden = false;
        state = clone(state); state.storedLogo = rec.name;
        var em = root.querySelector('[data-f="logoEmail"]');
        if (em && !em.checked && !trim(state.logoUrl)) { em.checked = true; em.dispatchEvent(new Event('change', { bubbles: true })); }
      });
    }

    // ---- read / write the static inputs ----
    function readStatic() {
      $$('[data-qty]').forEach(function (el) { state.qty[el.getAttribute('data-qty')] = el.value; });
      $$('[data-boxes]').forEach(function (el) { state.boxes[el.getAttribute('data-boxes')] = el.value; });
      $$('[data-addon]').forEach(function (el) { state.addon[el.getAttribute('data-addon')] = el.checked; });
      $$('[data-names]').forEach(function (el) { state.names[el.getAttribute('data-names')] = el.value; });
      $$('[data-names-sheet]').forEach(function (el) { state.namesSheet[el.getAttribute('data-names-sheet')] = el.checked; });
      $$('[data-f]').forEach(function (el) {
        var f = el.getAttribute('data-f');
        if (el.type === 'radio') { if (el.checked) state[f] = el.getAttribute('data-value'); }
        else if (el.type === 'checkbox') state[f] = el.checked;
        else state[f] = el.value;
      });
    }
    function writeStatic() {
      $$('[data-qty]').forEach(function (el) { el.value = state.qty[el.getAttribute('data-qty')] || ''; });
      $$('[data-boxes]').forEach(function (el) { el.value = state.boxes[el.getAttribute('data-boxes')] || ''; });
      $$('[data-addon]').forEach(function (el) { el.checked = !!state.addon[el.getAttribute('data-addon')]; });
      $$('[data-names]').forEach(function (el) { el.value = state.names[el.getAttribute('data-names')] || ''; });
      $$('[data-names-sheet]').forEach(function (el) { el.checked = !!state.namesSheet[el.getAttribute('data-names-sheet')]; });
      $$('[data-f]').forEach(function (el) {
        var f = el.getAttribute('data-f');
        if (el.type === 'radio') el.checked = state[f] === el.getAttribute('data-value');
        else if (el.type === 'checkbox') el.checked = !!state[f];
        else el.value = state[f] == null ? '' : state[f];
      });
    }

    // ---- the two tables ----
    function cellHtml(field, row, col, aria) {
      return '<div class="bb-co__cell bb-co__cell--qty"><span class="bb-co__cell-label" aria-hidden="true">' + esc(col.label) + '</span>' +
        '<input class="field__input bb-co__input bb-co__input--qty" type="number" min="0" inputmode="numeric" data-cell="' + field +
        '" data-row="' + row.id + '" data-col="' + esc(col.id) + '" value="' + esc(row.q[col.id] || '') + '" aria-label="' + esc(aria) + '"></div>';
    }
    function renderSplit(v) {
      var head = $('[data-split-head]'), body = $('[data-split-rows]');
      if (!head || !body) return;
      root.style.setProperty('--bb-co-cols', String(Math.max(v.cols.length, 1)));
      root.classList.toggle('is-cols-few', v.cols.length <= 3);
      head.innerHTML = '<span>Name</span><span>Address</span>' + v.cols.map(function (c) { return '<span>' + esc(c.label) + '</span>'; }).join('') + '<span></span>';
      body.innerHTML = state.addrs.map(function (a, i) {
        var name = trim(a.label) || 'address ' + (i + 1);
        return '<div class="bb-co__row bb-co__row--split" role="group" aria-label="Address ' + (i + 1) + '" data-row-id="' + a.id + '">' +
          '<div class="bb-co__cell bb-co__cell--name"><span class="bb-co__cell-label" aria-hidden="true">Name</span><input class="field__input bb-co__input" type="text" placeholder="Name this address" aria-label="Name for address ' + (i + 1) + '" data-rowf="addrs" data-row="' + a.id + '" data-key="label" value="' + esc(a.label) + '"></div>' +
          '<div class="bb-co__cell bb-co__cell--street"><span class="bb-co__cell-label" aria-hidden="true">Address</span><input class="field__input bb-co__input" type="text" autocomplete="off" placeholder="Street, city, state, ZIP" aria-label="Street address for ' + esc(name) + '" data-rowf="addrs" data-row="' + a.id + '" data-key="street" value="' + esc(a.street) + '"></div>' +
          v.cols.map(function (c) { return cellHtml('addrs', a, c, c.label + ' to ' + name); }).join('') +
          '<div class="bb-co__cell bb-co__cell--remove">' + (state.addrs.length > 1
            ? '<button type="button" class="bb-co__link" data-remove-address="' + a.id + '">Remove<span class="visually-hidden"> ' + esc(name) + '</span></button>' : '') + '</div>' +
          '</div>';
      }).join('');
    }
    function renderDrops(v) {
      var head = $('[data-drop-head]'), body = $('[data-drop-rows]');
      if (!head || !body) return;
      head.innerHTML = '<span>Arrives by</span>' + v.cols.map(function (c) { return '<span>' + esc(c.label) + '</span>'; }).join('') + '<span></span>';
      body.innerHTML = state.drops.map(function (r, i) {
        var label = i === 0 ? 'First release arrives by' : 'Release ' + (i + 1) + ' arrives by';
        return '<div class="bb-co__row bb-co__row--drop" role="group" aria-label="Release ' + (i + 1) + '" data-row-id="' + r.id + '">' +
          '<div class="bb-co__cell bb-co__cell--date"><span class="bb-co__cell-label' + (i === 0 ? ' bb-co__cell-label--first' : '') + '">' + esc(label) + '</span>' +
          '<input class="field__input bb-co__input" type="date" min="' + today + '" aria-label="' + esc(label) + '" data-rowf="drops" data-row="' + r.id + '" data-key="date" value="' + esc(r.date) + '"></div>' +
          v.cols.map(function (c) { return cellHtml('drops', r, c, c.label + ', release ' + (i + 1)); }).join('') +
          '<div class="bb-co__cell bb-co__cell--remove"><button type="button" class="bb-co__link" data-remove-release="' + r.id + '">Remove<span class="visually-hidden"> release ' + (i + 1) + '</span></button></div>' +
          '</div>';
      }).join('');
    }
    // Write only when something changed, so screen readers hear a live region once, not on every keystroke.
    function putText(el, text) { if (el && el.textContent !== text) el.textContent = text; }
    function putHtml(el, html) { if (el && el.__bbco !== html) { el.innerHTML = html; el.__bbco = html; } }
    function putHidden(el, hidden) { if (el && el.hidden !== hidden) el.hidden = hidden; }
    function renderTotals(el, totals) {
      if (!el) return;
      putHtml(el, totals.map(function (t) {
        return '<span class="bb-co__total' + (t.ok ? ' is-ok' : ' is-off') + '">' + esc(t.text) + '</span>';
      }).join(''));
      putHidden(el, totals.length === 0);
    }
    function setText(sel, text) { $$(sel).forEach(function (el) { putText(el, text); }); }

    // ---- paint the page from the state ----
    function update(rebuild) {
      var v = view(state, cfg, today);
      root.setAttribute('data-ship', state.ship);

      [1, 2, 3, 4].forEach(function (n) {
        var s = v.steps[n], sec = $('[data-step="' + n + '"]');
        if (!sec) return;
        sec.classList.toggle('is-open', s.open);
        sec.classList.toggle('is-done', s.done);
        sec.classList.toggle('is-waiting', s.waiting);
        var body = sec.querySelector('[data-step-body]');
        if (body) body.hidden = !s.open;
        var sum = sec.querySelector('[data-step-summary]');
        if (sum) { sum.textContent = s.summary; sum.hidden = !s.summary; }
        var ed = sec.querySelector('[data-edit]');
        if (ed) { ed.hidden = !s.canEdit; ed.setAttribute('aria-expanded', s.open ? 'true' : 'false'); }
        var cont = sec.querySelector('[data-continue]');
        if (cont) {
          // Only one filled button in view: once the order is ready, the quote button takes it.
          cont.classList.toggle('button', !v.ready);
          cont.classList.toggle('button-secondary', v.ready);
        }
      });

      cfg.pieces.forEach(function (p) {
        var box = $('[data-names-block="' + p.id + '"]');
        putHidden(box, !v.names[p.id]);
        setText('[data-names-math="' + p.id + '"]', v.names[p.id] || '');
        var tile = $('[data-piece="' + p.id + '"]');
        if (tile) tile.classList.toggle('is-on', v.ids.indexOf(p.id) >= 0);
      });
      $$('[data-names-sheet]').forEach(function (el) {
        var ta = $('[data-names="' + el.getAttribute('data-names-sheet') + '"]');
        if (ta) ta.disabled = el.checked;
      });
      var bm = $('[data-below-min]');
      putText(bm, v.belowMinText);
      putHidden(bm, !v.hasBelowMin);

      setText('[data-approve-line]', v.approve.line);
      $$('[data-approve-line]').forEach(function (el) { el.classList.toggle('is-strong', v.approve.state !== 'none'); });
      $$('[data-season-line]').forEach(function (el) { putHidden(el, !v.seasonLine); });

      if (rebuild) { renderSplit(v); renderDrops(v); }
      renderTotals($('[data-split-totals]'), v.splitTotals);
      renderTotals($('[data-drop-totals]'), v.dropTotals);
      $$('[data-preset]').forEach(function (b) { b.setAttribute('aria-pressed', state.preset === b.getAttribute('data-preset') ? 'true' : 'false'); });

      var pieces = $('[data-sum="pieces"]');
      putHtml(pieces, v.sumPieces.map(function (w) { return '<span>' + esc(w) + '</span>'; }).join(''));
      setText('[data-sum="logo"]', v.sumLogo);
      setText('[data-sum="delivery"]', v.delivery);
      var where = $('[data-sum="where"]');
      putHtml(where, v.where.map(function (w) { return '<span>' + esc(w) + '</span>'; }).join(''));
      putHidden(where, !v.where.length);
      setText('[data-sum="inhand-label"]', v.inhandLabel);
      setText('[data-sum="inhand"]', v.sumInhand);
      setText('[data-sum="approve"]', v.sumApprove);
      $$('[data-sum="inhand"], [data-sum="approve"]').forEach(function (el) { el.classList.toggle('is-muted', !parseISO(v.base)); });
      $$('[data-sum="pieces"], [data-sum="logo"]').forEach(function (el) { el.classList.toggle('is-muted', el.textContent === TEXT.nothing); });

      var bl = $('[data-block-lines]');
      putHtml(bl, v.panelLines.map(function (t) { return '<span>' + esc(t) + '</span>'; }).join(''));
      putHidden(bl, !v.panelLines.length);
      var submit = $('[data-submit]');
      if (submit) submit.setAttribute('aria-disabled', v.blocked ? 'true' : 'false');
      // House: on phones, a slim bar keeps the order in view until the summary itself scrolls in.
      var bar = $('[data-co-bar]');
      if (bar) {
        var n = v.ids.length;
        putHidden(bar, !n);
        setText('[data-co-bar-text]', n ? plural(n, 'item', 'items') + (v.approve.date ? ' · Approve by ' + fmtDate(v.approve.date).replace(/, \d{4}$/, '') : '') : '');
      }
      var em = $('[data-f="email"]');
      if (em) {
        if (state.showAll && !v.emailOk) em.setAttribute('aria-invalid', 'true');
        else em.removeAttribute('aria-invalid');
      }
      return v;
    }

    function focusStep(n) {
      var h = $('[data-step="' + n + '"] [data-step-heading]');
      if (!h) return;
      h.focus({ preventScroll: true });
      var sec = $('[data-step="' + n + '"]');
      if (sec && sec.scrollIntoView) {
        var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        sec.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
      }
    }

    // ---- events ----
    function onField(e) {
      var t = e.target;
      if (!t || !t.getAttribute) return;
      var id;
      if ((id = t.getAttribute('data-qty')) !== null) {
        var q = {}; q[id] = t.value;
        state = setPieces(state, { qty: q }, cfg, today); update(true); return;
      }
      if ((id = t.getAttribute('data-boxes')) !== null) {
        var b = {}; b[id] = t.value;
        state = setPieces(state, { boxes: b }, cfg, today); update(true); return;
      }
      if ((id = t.getAttribute('data-addon')) !== null) { state.addon = withMap(state.addon, id, t.checked); update(false); return; }
      if ((id = t.getAttribute('data-names')) !== null) { state.names = withMap(state.names, id, t.value); update(false); return; }
      if ((id = t.getAttribute('data-names-sheet')) !== null) { state.namesSheet = withMap(state.namesSheet, id, t.checked); update(false); return; }
      var cell = t.getAttribute('data-cell');
      if (cell) { state = setCell(state, cell, +t.getAttribute('data-row'), t.getAttribute('data-col'), t.value); update(false); return; }
      var rowf = t.getAttribute('data-rowf');
      if (rowf) { state = setRowField(state, rowf, +t.getAttribute('data-row'), t.getAttribute('data-key'), t.value); update(false); return; }
      var f = t.getAttribute('data-f');
      if (!f) return;
      if (f === 'inhand') { state = setInhand(state, t.value, cfg, today); update(true); return; }
      if (f === 'ship') { if (t.checked) { state = setShip(state, t.getAttribute('data-value'), cfg, today); update(true); } return; }
      state = clone(state);
      state[f] = t.type === 'checkbox' ? t.checked : t.value;
      update(false);
    }
    // Enter in steps 1 to 3 never sends the form: in a text field it presses that step's Continue.
    root.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' || e.isComposing) return;
      var t = e.target;
      if (!t || !t.closest || t.tagName === 'TEXTAREA' || t.tagName === 'BUTTON' || t.tagName === 'A') return;
      var sec = t.closest('[data-step]');
      var n = sec ? +sec.getAttribute('data-step') : 0;
      if (n < 1 || n > 3 || t.tagName !== 'INPUT') return;
      e.preventDefault();
      if (t.type === 'checkbox' || t.type === 'radio') return;
      var cont = sec.querySelector('[data-continue]');
      if (cont && !cont.hidden && !cont.closest('[hidden]')) cont.click();
    });
    root.addEventListener('input', onField);
    root.addEventListener('change', onField);

    root.addEventListener('click', function (e) {
      var b = e.target.closest ? e.target.closest('button') : null;
      if (!b || !root.contains(b)) return;
      var n;
      if ((n = b.getAttribute('data-continue')) !== null) {
        state = go(state, +n + 1); update(false); focusStep(state.step);
      } else if ((n = b.getAttribute('data-edit')) !== null) {
        state = go(state, +n); update(false); focusStep(state.step);
      } else if (b.hasAttribute('data-add-address')) {
        state = addAddress(state); update(true);
        var rows = $$('[data-split-rows] .bb-co__row');
        var first = rows.length && rows[rows.length - 1].querySelector('input');
        if (first) first.focus();
      } else if ((n = b.getAttribute('data-remove-address')) !== null) {
        state = removeAddress(state, +n); update(true);
        var add = $('[data-add-address]'); if (add) add.focus();
      } else if (b.hasAttribute('data-add-release')) {
        state = addRelease(state, today); update(true);
        var drows = $$('[data-drop-rows] .bb-co__row');
        var d = drows.length && drows[drows.length - 1].querySelector('input');
        if (d) d.focus();
      } else if ((n = b.getAttribute('data-remove-release')) !== null) {
        state = removeRelease(state, +n); update(true);
        var addr = $('[data-add-release]'); if (addr) addr.focus();
      } else if ((n = b.getAttribute('data-preset')) !== null) {
        state = pickPreset(state, n, cfg, today); update(true);
      }
    });

    // ---- submit: gate, then write the named fields and the summary ----
    var rawNames = [];
    function stripRaw() {
      rawNames = [];
      $$('[data-raw][name]').forEach(function (el) { rawNames.push([el, el.getAttribute('name')]); el.removeAttribute('name'); });
    }
    function restoreRaw() {
      rawNames.forEach(function (p) { p[0].setAttribute('name', p[1]); });
      rawNames = [];
    }
    form.addEventListener('submit', function (e) {
      readStatic();
      var v = view(state, cfg, today);
      if (v.blocked) {
        e.preventDefault();
        state = clone(state); state.showAll = true;
        if (v.reasons.length === 0 && !v.emailOk) {
          state = go(state, 4);
          update(false);
          var em = $('[data-f="email"]');
          if (em) em.focus();
        } else {
          update(false);
          var lines = $('[data-block-lines]'); if (lines) lines.focus();
        }
        return;
      }
      try {
        // One readable summary in contact[body]; the raw step fields are dropped so nothing is sent twice.
        var body = $('[data-body]');
        if (body) body.value = orderBody(state, cfg, today);
        stripRaw();
        var ss = store();
        if (ss) {
          ss.setItem(draftKey, JSON.stringify({
            state: state,
            confirm: { first: trim(state.name).split(/\s+/)[0] || '', lines: confirmLines(state, cfg, today) }
          }));
        }
      } catch (err) {
        restoreRaw(); // post the raw fields rather than nothing
      }
    });
    window.addEventListener('pageshow', function (ev) { if (ev.persisted) restoreRaw(); });

    // ---- start ----
    // The pieces picked on their private page go first in the list, each marked, so the quote starts from them.
    function showPicked(ids) {
      var list = root.querySelector('.bb-co__pieces');
      if (!list || !ids || !ids.length) return;
      ids.slice().reverse().forEach(function (id) {
        var row = list.querySelector('[data-piece="' + id + '"]');
        if (!row) return;
        list.insertBefore(row, list.firstChild);
        var body = row.querySelector('.bb-co__piece-body');
        if (body && !body.querySelector('.bb-co__picked')) {
          var tag = document.createElement('p');
          tag.className = 'bb-co__picked';
          tag.textContent = 'On your page';
          body.insertBefore(tag, body.firstChild);
        }
      });
    }

    readStatic();
    if (root.querySelector('[data-bb-co-errored]')) {
      // Shopify sent the form back (usually the email). Restore what was typed and open step 4.
      var ss2 = store(), draft = null;
      try { draft = ss2 && JSON.parse(ss2.getItem(draftKey) || 'null'); } catch (e2) { draft = null; }
      if (draft && draft.state) { state = draft.state; writeStatic(); showPicked(state.picked); }
      state = go(state, 4);
      state.reach = 4;
      state.showAll = true;
    } else {
      var pre = prefillFromUrl(state, window.location.search, cfg);
      if (pre !== state) { state = pre; writeStatic(); showPicked(state.picked); }
      if (state.ship !== 'one') state = setShip(state, state.ship, cfg, today);
    }
    state = setPieces(state, {}, cfg, today);
    update(true);
    return { get state() { return state; } };
  }

  // If anything goes wrong while wiring up, fall back to the no-JS page: every step open, raw fields posted.
  function degrade(root) {
    root.classList.remove('is-enhanced');
    Array.prototype.forEach.call(root.querySelectorAll('[data-step-body]'), function (b) { b.hidden = false; });
    Array.prototype.forEach.call(root.querySelectorAll('[data-js-only]'), function (b) { b.hidden = true; });
    var form = root.querySelector('form');
    if (form) form.noValidate = false;
  }

  if (!window.customElements.get('bb-company-order')) {
    window.customElements.define('bb-company-order', class extends HTMLElement {
      connectedCallback() {
        if (this._bbco) return;
        try {
          this._bbco = mount(this) || {};
        } catch (e) {
          this._bbco = {};
          degrade(this);
          if (window.console) console.error(e);
        }
      }
    });
  }
})();
